import { routedClient } from "react-migration/lib/persistence/apollo";
import { uniqBy } from "lodash";
import { feature, Polygon } from "@turf/helpers";
import union from "@turf/union";
import { SelectionType } from "src/js/stores/map/store";
import { getTitleGeometryByTitleNo } from "react-migration/domains/ownership/apollo/getTitleGeometryByTitleNo";
import { zoomToSelection } from "react-migration/lib/map/zoomToSelection";
import { SearchAutocompleteSection } from "../../types";
import PARCEL_SEARCH_QUERY from "./parcelSearch.gql";

interface ParcelSearchResponse {
  title_no: string;
  parcel_identifier: string;
}

interface ParcelSearchByParcelIdResponse {
  parcelSearchByParcelId: ParcelSearchResponse[];
}

interface ParcelSearchByParcelIdVariables {
  partialParcelId: string;
  limit: number;
}

const EMPTY_MATCHES: ParcelSearchResponse[] = [];

async function searchParcelsByParcelId(partialParcelId?: string, signal?: AbortSignal) {
  if (!partialParcelId || partialParcelId.length < 3) {
    return {
      matches: EMPTY_MATCHES,
      loading: false,
      error: null,
    };
  }

  try {
    const { data } = await routedClient.query<ParcelSearchByParcelIdResponse, ParcelSearchByParcelIdVariables>({
      query: PARCEL_SEARCH_QUERY,
      variables: { partialParcelId, limit: 10 },
      context: {
        fetchOptions: {
          signal,
        },
      },
    });

    return {
      matches: data?.parcelSearchByParcelId ?? EMPTY_MATCHES,
      loading: false,
      error: null,
    };
  } catch (error) {
    // TODO - invert this?
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }

    return {
      matches: EMPTY_MATCHES,
      loading: false,
      error,
    };
  }
}

export const titlesAutocompleteSection: SearchAutocompleteSection = {
  id: "titlesUk",
  title: "Titles",
  async fetchResults(search: string, signal?: AbortSignal) {
    const results = await searchParcelsByParcelId(search, signal);

    return uniqBy(
      results.matches.map((result) => ({
        id: result.title_no,
        title: result.title_no,
      })),
      "id"
    );
  },
  async onSelectionHandler({ id: titleNo }, { map, setSelection }) {
    const result = await getTitleGeometryByTitleNo(titleNo);
    const geometries = result?.polygons || [];
    const polygonFeatures = geometries.map((g) => feature(g.geometry as Polygon));
    const selectionFeature = union(...polygonFeatures);

    if (!geometries.length || !selectionFeature.geometry) {
      throw new Error(`No geometry found for title ${titleNo}`);
    }

    zoomToSelection(selectionFeature.geometry, map);

    setSelection({
      type: SelectionType.OWNERSHIP_TITLE,
      id: titleNo,
      feature: selectionFeature,
    });
  },
};

export const parcelsAutocompleteSection: SearchAutocompleteSection = {
  id: "parcelsUs",
  title: "Parcels",
  async fetchResults(search: string, signal?: AbortSignal) {
    const results = await searchParcelsByParcelId(search, signal);

    return uniqBy(
      results.matches.map((result) => ({
        id: result.title_no,
        title: result.parcel_identifier,
      })),
      "id"
    );
  },
  onSelectionHandler: titlesAutocompleteSection.onSelectionHandler,
};
